import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@vnetwork/ui/feedback';
import {
  currentWorkspaceRoute,
  planBack,
  planRevoked,
  planSwitch,
  sameId,
  useOrgCustomerId,
  useOrgWorkspaces,
  useWorkingProject,
  type StoredProject,
} from '@/api/iam';
import { WORKSPACE_SERVICES } from '@/lib/workspace-services';
import { noAccessText, switchedText } from './model';

const TOAST_MS = 4000;
/** The services whose pages the project choice may move; the others' sources are not fetched by the top bar. */
const SWITCHABLE = WORKSPACE_SERVICES.filter((s) => s.switchable);

/** The workspaces of the switchable services, and whether they have all answered (a failed answer counts). */
function useSwitchableWorkspaces() {
  return useOrgWorkspaces(SWITCHABLE);
}

/**
 * Storing the choice and moving the page with it. Choosing a project stores it, and when the open page is inside a
 * workspace of another organization the page follows (`planSwitch`, history replace, a toast). `back` is set only while
 * a foreign workspace is open: it stores "all projects" and goes to the same page type in the home workspace, or to the
 * service's landing when there is none (`planBack`).
 */
export function useApplyProjectChoice() {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // The registry reads the workspace from the path (Compute) or the search (Kubernetes).
  const loc = pathname + search;
  const toast = useToast();
  const home = useOrgCustomerId();
  const { choose } = useWorkingProject();
  const { workspaces, settled } = useSwitchableWorkspaces();

  const foreign = useMemo(() => {
    const current = currentWorkspaceRoute(SWITCHABLE, loc, workspaces);
    return current && home && !sameId(current.match.organizationId, home) ? current : null;
  }, [loc, workspaces, home]);

  const apply = useCallback(
    (next: StoredProject | null, label: string) => {
      choose(next);
      if (!next) return;
      const plan = planSwitch({ registry: SWITCHABLE, loc, choice: { organizationId: next.organizationId }, workspaces });
      if (plan.kind !== 'navigate') return;
      navigate(plan.to, { replace: true });
      toast.info(switchedText(label, plan.toList), { duration: TOAST_MS });
    },
    [choose, loc, workspaces, navigate, toast],
  );

  const back = useMemo(
    () =>
      foreign
        ? () => {
            choose(null);
            const plan = planBack({ registry: SWITCHABLE, loc, workspaces });
            if (plan.kind !== 'navigate') return;
            navigate(plan.to, { replace: true });
            toast.info(switchedText('your organization', plan.toList), { duration: TOAST_MS });
          }
        : null,
    [foreign, choose, loc, workspaces, navigate, toast],
  );

  return { apply, back, workspacesSettled: settled };
}

/**
 * A stored choice that is no longer usable (`revoked`): `planRevoked` says whether to clear it and whether to leave the
 * workspace of that choice's organization for the service's landing, which also says so once (E4).
 */
export function useRevokedChoice(revoked: boolean, stored: StoredProject | null, clear: () => void) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const loc = pathname + search;
  const toast = useToast();
  const { workspaces, settled, failed } = useSwitchableWorkspaces();

  useEffect(() => {
    const plan = planRevoked({ registry: SWITCHABLE, loc, revoked, stored, workspaces, settled, failed });
    if (plan.redirect) {
      const name = workspaces.find((w) => sameId(w.organizationId, stored?.organizationId))?.organizationName;
      navigate(plan.redirect, { replace: true });
      toast.info(noAccessText(name || 'that organization'), { duration: TOAST_MS });
    }
    if (plan.clear) clear();
  }, [revoked, stored, loc, workspaces, settled, failed, navigate, toast, clear]);
}
